import { ActionIcon, Box, Group, Loader, Tooltip } from '@mantine/core';
import { GroupExpandToolbar, StatColumn } from '../../../common';
import {
    IdColumn,
    MediaContentColumn,
    TypeColumn,
    VariationsColumn,
} from './columns';
import { useDisclosure, useToggle } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';

import CampaignGroupStatsTimelineModal from '../../../modals/timeline/CampaignGroupStatsTimelineModal';
import CheckboxColumn from '../../../common/tables/columns/CheckboxColumn';
import { IconChartDots2 } from '@tabler/icons-react';
import { MantineReactTable } from 'mantine-react-table';
import MediaTableToolbar from '../toolbars/MediaToolbarActions';
import classes from '../GroupTable.module.css';
import { hasRole } from '../../../../utils/roles';
import { naiveRound } from '../../../../utils/text';
import { sumObjectKeys } from '../../../../utils/statUtils';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';
import useGetMediaGroupByIdQuery from '../../../../http/queries/groups/useGetMediaGroupById';
import useToggleGroupProperty from '../../../../http/mutations/groups/useToggleGroupProperty';

const MediaTable = ({ groupId }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const [rowSelection, setRowSelection] = useState({});
    const [tableData, setTableData] = useState([]);
    const [showDeleted, toggleShowDeleted] = useToggle();

    const [modalRow, setModalRow] = useState(null);
    const [
        timelineModalOpen,
        { open: openTimelineModal, close: closeTimelineModal },
    ] = useDisclosure();

    const { isLoading, data: mediaGroup } = useGetMediaGroupByIdQuery(
        groupId,
        groupId != null
    );

    const media = mediaGroup?.media;
    useEffect(() => {
        if (!media) {
            return;
        }

        if (showDeleted) {
            setTableData(media);
        } else {
            setTableData(media.filter((x) => !x.deleted));
        }
    }, [media, showDeleted]);

    const { mutate: setActive } = useToggleGroupProperty({
        type: 'media',
        property: 'active',
    });

    const { mutate: setApproved } = useToggleGroupProperty({
        type: 'media',
        property: 'approved',
    });

    const { mutate: setDeleted } = useToggleGroupProperty({
        type: 'media',
        property: 'deleted',
    });

    const handleTimeline = (rowData) => {
        setModalRow(rowData);
        openTimelineModal();
    };

    const handleActiveChange = (rowData, value, e) => {
        setActive({
            id: rowData.id,
            value: value,
        });
    };

    const handleApprovedChanged = (rowData, value, e) => {
        setApproved({
            id: rowData.id,
            value: value,
        });
    };

    const handleDeleteChange = (rowData, value) => {
        setDeleted({
            id: rowData.id,
            value: value,
        });
    };

    const columns = useMemo(
        () =>
            [
                IdColumn(),
                MediaContentColumn(),
                StatColumn('Sent', (x) => x.stats?.send),
                StatColumn('Clicks', (x) =>
                    sumObjectKeys(x.stats, ['uniqueClick'])
                ),
                StatColumn('Previews', (x) =>
                    sumObjectKeys(x.stats, ['uniquePreview'])
                ),
                StatColumn(
                    'CTR',
                    (x) => {
                        const uniqueClicks = sumObjectKeys(x.stats, [
                            'uniqueClick',
                        ]);
                        return naiveRound(
                            (uniqueClicks / x.stats?.send) * 100,
                            2
                        );
                    },
                    true
                ),
                StatColumn('Conversions', (x) => x.stats?.conversion),
                StatColumn(
                    'CR',
                    (x) => {
                        const uniqueClicks = sumObjectKeys(x.stats, [
                            'uniqueClick',
                        ]);
                        return naiveRound(
                            (x.stats?.conversion / uniqueClicks) * 100,
                            2
                        );
                    },
                    true
                ),
                CheckboxColumn({
                    header: 'Active',
                    accessorKey: 'active',
                    onChange: handleActiveChange,
                }),
                hasRole(userProfile, 'SUPER_ADMIN')
                    ? CheckboxColumn({
                          header: 'Approved',
                          accessorKey: 'approved',
                          onChange: handleApprovedChanged,
                      })
                    : null,
                CheckboxColumn({
                    header: 'Hide',
                    accessorKey: 'deleted',
                    onChange: handleDeleteChange,
                }),
                /*PropertyColumn({
                form,
                availableFilters,
                isLoading,
            }),
            OperatorColumn({
                form,
                availableFilters,
                isLoading,
            }),
            ValuesColumn({ form, availableFilters, isLoading }),*/
            ].filter((x) => x != null),
        [isLoading, userProfile]
    );

    if (isLoading) {
        return (
            <Group
                w={'100%'}
                mih={100}
                justify={'center'}>
                <Loader />
            </Group>
        );
    }

    const tableProps = {
        //expandRowsFn: true, // Assuming this should be true, since it was provided without a value
        columns: columns,
        data: tableData,
        enableSorting: false,
        enableColumnActions: false,
        enableDensityToggle: false,

        enableHiding: false,
        enableFilters: false,
        enableEditing: false,
        enableResizing: true,
        enableFullScreenToggle: false,
        defaultColumn: {
            minSize: 0,
            maxSize: Number.MAX_SAFE_INTEGER,
        },

        state: { rowSelection },
        mantineTopToolbarProps: () => ({
            className: classes.toolbar,
        }),
        enableTopToolbar: true,
        renderTopToolbarCustomActions: ({ props: table }) => (
            <MediaTableToolbar
                table={table}
                mediaGroup={mediaGroup}
                showHidden={showDeleted}
                toggleShowHidden={toggleShowDeleted}
            />
        ),
        enablePagination: true,
        manualPagination: false,
        enableBottomToolbar: true,
        enableRowActions: true,
        positionActionsColumn: 'last',
        mantineTableContainerProps: {
            style: {
                minHeight: '100px',
            },
        },
        mantineTableBodyRowProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => {
            return {
                className: classes.rowBox,
            };
        },

        mantineTableBodyCellProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => {
            return {
                className: classes.cellBox,
            };
        },

        renderRowActions: ({ table, row }) => (
            <>
                <Tooltip label={'Send Timeline'}>
                    <ActionIcon
                        variant='default'
                        size={'sm'}
                        onClick={() => handleTimeline(row.original)}>
                        <IconChartDots2 size={16} />
                    </ActionIcon>
                </Tooltip>
            </>
        ),
        state: {
            density: 'xs',
        },
        meta: {
            dataLength: tableData.length,
            originalLength: media.length,
        },
    };
    return (
        <>
            <MantineReactTable {...tableProps} />
            <CampaignGroupStatsTimelineModal
                resource={'media'}
                resourceId={modalRow?.id}
                title={`Send Timeline - Media ID ${modalRow?.id}`}
                opened={timelineModalOpen}
                close={closeTimelineModal}
            />
        </>
    );
};
export default MediaTable;
